/**
 * Lớp vận chuyển thật: gọi Platform API ở `/api/v1/...` qua Gateway (đặc tả
 * Chương 8). Giao diện không bao giờ gọi thẳng một service.
 *
 * Bản giả cùng vai là `mock/transport.ts`. Repository chọn một trong hai bằng
 * `dungApiThat()`, nên view và hook không biết dữ liệu thật hay giả.
 *
 * Mọi lỗi ra khỏi đây đều là `ApiError` đã chuẩn hoá từ Problem Details
 * (RFC 9457, ADR-0020): `code` là `error_code` của backend, `message` là
 * `detail` tiếng Việt, `details` là mảng `errors` để form gắn lỗi vào đúng ô.
 */
import { getAccessToken, oidcEnabled } from '../auth/oidc';
import type { ApiError, LoiTruong } from '../errors';
import { choTruocKhiThuLai, SO_LAN_THU_429 } from './thu-lai-429';

/**
 * Đọc dữ liệu thật hay dữ liệu mẫu.
 *
 * - Có `?mock=` trên URL: luôn dữ liệu mẫu, để soát bốn trạng thái bằng mắt kể
 *   cả khi đã đăng nhập thật (README, mục "Xem từng trạng thái").
 * - Đăng nhập Keycloak thật (`/config.json` có cấu hình): API thật.
 * - Còn lại, tức chạy `pnpm dev` trơn với tài khoản giả: dữ liệu mẫu. Gọi API
 *   thật lúc này chỉ ra 401, vì không có token nào để gắn.
 *
 * Đọc lúc gọi chứ không lúc nạp module: `?mock=` đổi được giữa hai lần điều
 * hướng mà không tải lại trang.
 */
export function dungApiThat(): boolean {
  if (typeof window === 'undefined') return false;
  if (new URLSearchParams(window.location.search).has('mock')) return false;
  return oidcEnabled();
}

export interface YeuCauApi {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /**
   * Tham số query; giá trị `undefined` hoặc rỗng bị bỏ, không gửi `?status=`.
   * Mảng gửi lặp tham số (`?action=GET&action=POST`, `style: form, explode`).
   */
  query?: Record<string, string | number | readonly string[] | undefined>;
  body?: unknown;
  /** Header `Idempotency-Key`, bắt buộc với POST tạo tài nguyên (mục 6.1). */
  idempotencyKey?: string;
  /**
   * Header `If-Match` — ghi có điều kiện theo `ETag` đọc trước đó (ADR-0004:
   * `PUT /users/{id}/roles`). Lệch phiên bản thì server trả `412`.
   */
  ifMatch?: string;
  /** Header `Accept`; mặc định JSON và Problem Details. */
  accept?: string;
  /**
   * Thân gửi nguyên văn thay cho `body` JSON — tệp người dùng chọn, ví dụ
   * license JWS gửi tới `POST /internal/v1/license/import` (ADR-0058 mục 6.1).
   */
  thanTho?: { duLieu: Blob | string; kieu: string };
  /**
   * Biểu mẫu `multipart/form-data` — tệp tải lên kèm vài trường chữ, ví dụ tệp
   * tri thức (`POST /api/v1/m/tri-thuc/files`). Không tự đặt `Content-Type`:
   * trình duyệt ghép `boundary` vào header, đặt tay là server không đọc được.
   */
  bieuMau?: FormData;
}

/**
 * Mã phía giao diện (không phải `error_code` của backend) cho trường hợp máy chủ
 * trả lời nhưng Platform API phía sau chưa chạy: proxy trả trang lỗi HTML, nginx
 * của image giao diện trả `503` vì chưa cấu hình Gateway (ADR-0052), hoặc `/api`
 * chưa được định tuyến nên nhận về `index.html`. Khác `NETWORK_ERROR`: mạng của
 * người dùng vẫn tốt, việc cần làm là chờ hoặc báo đội vận hành.
 */
const API_CHUA_SAN_SANG = 'API_UNAVAILABLE';

/**
 * Mã lỗi khi phản hồi lỗi **không** mang `error_code` — thường là trang lỗi
 * của proxy đứng trước Gateway. 502–504 nghĩa là proxy không tới được Gateway
 * hay service phía sau.
 */
function maTheoHttp(status: number): string {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'PERMISSION_DENIED';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 502 && status <= 504) return API_CHUA_SAN_SANG;
  return 'INTERNAL';
}

interface Problem {
  error_code?: string;
  title?: string;
  detail?: string;
  errors?: LoiTruong[];
  /** Thân lỗi của chính APISIX, không phải Problem Details: `{"error_msg": "404 Route Not Found"}`. */
  error_msg?: string;
  /** Chỉ có ở `403 SAI_DIA_CHI_TRUY_CAP` (ADR-0097): địa chỉ chung của cổng tổ chức. */
  correct_base_url?: string;
}

async function docLoi(res: Response): Promise<ApiError> {
  let problem: Problem | null = null;
  try {
    problem = (await res.json()) as Problem;
  } catch {
    // Thân không phải JSON (trang lỗi HTML của proxy): lỗi 5xx là backend chưa
    // trả lời, 4xx lùi về mã theo HTTP.
    if (res.status >= 500) return { code: API_CHUA_SAN_SANG, status: res.status, message: '' };
  }
  // `503 INTERNAL`: service báo một phụ thuộc đang hỏng (ADR-0020), hoặc image
  // giao diện chưa nối Gateway. Cả hai đều là "chưa sẵn sàng, thử lại sau",
  // không phải lỗi ngoài dự kiến.
  if (res.status === 503 && (problem?.error_code ?? 'INTERNAL') === 'INTERNAL') {
    return { code: API_CHUA_SAN_SANG, status: res.status, message: '' };
  }
  // `404` do APISIX tự trả: Gateway chưa có route cho đường này, tức module hay service phía sau chưa được triển khai
  // trên máy chủ. Báo "chưa sẵn sàng" chứ không phải "không tìm thấy dữ liệu", kẻo người dùng tưởng tổ chức chưa có gì.
  if (res.status === 404 && !problem?.error_code && problem?.error_msg) {
    return { code: API_CHUA_SAN_SANG, status: res.status, message: '' };
  }
  return {
    code: problem?.error_code ?? maTheoHttp(res.status),
    status: res.status,
    message: problem?.detail ?? '',
    details: problem?.errors,
    ...(problem?.correct_base_url ? { correctBaseUrl: problem.correct_base_url } : {}),
  };
}

/** Gửi yêu cầu, chuẩn hoá mọi lỗi thành `ApiError`; trả `Response` đã `ok`. */
async function gui(duongDan: string, yeuCau: YeuCauApi): Promise<Response> {
  const url = new URL(duongDan, window.location.origin);
  for (const [khoa, giaTri] of Object.entries(yeuCau.query ?? {})) {
    if (Array.isArray(giaTri)) {
      for (const muc of giaTri) if (muc !== '') url.searchParams.append(khoa, muc);
    } else if (giaTri !== undefined && giaTri !== '') url.searchParams.set(khoa, String(giaTri));
  }

  const headers = new Headers({
    Accept: yeuCau.accept ?? 'application/json, application/problem+json',
  });
  if (yeuCau.thanTho) headers.set('Content-Type', yeuCau.thanTho.kieu);
  else if (yeuCau.bieuMau === undefined && yeuCau.body !== undefined) headers.set('Content-Type', 'application/json');
  if (yeuCau.idempotencyKey) headers.set('Idempotency-Key', yeuCau.idempotencyKey);
  if (yeuCau.ifMatch) headers.set('If-Match', yeuCau.ifMatch);

  // Token hết hạn mà làm mới không được thì keycloak-js ném: coi như 401, để
  // màn hình nói "đăng nhập lại" thay vì "mất mạng".
  let token: string | undefined;
  try {
    token = await getAccessToken();
  } catch {
    throw { code: 'UNAUTHENTICATED', status: 401, message: '' } satisfies ApiError;
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const method = yeuCau.method ?? 'GET';
  for (let lan = 0; ; lan++) {
    let res: Response;
    try {
      res = await fetch(url, {
        method,
        headers,
        body: yeuCau.thanTho
          ? yeuCau.thanTho.duLieu
          : yeuCau.bieuMau
            ? yeuCau.bieuMau
            : yeuCau.body === undefined
            ? undefined
            : JSON.stringify(yeuCau.body),
      });
    } catch {
      throw { code: 'NETWORK_ERROR', message: '' } satisfies ApiError;
    }

    // GET bị Gateway trả 429 thì chờ theo `Retry-After` rồi gửi lại — lý do ở `thu-lai-429.ts`.
    const cho = method === 'GET' && lan < SO_LAN_THU_429
      ? choTruocKhiThuLai(res.status, res.headers.get('Retry-After'))
      : undefined;
    if (cho !== undefined) {
      void res.body?.cancel();
      await new Promise((xong) => setTimeout(xong, cho));
      continue;
    }
    if (!res.ok) throw await docLoi(res);
    return res;
  }
}

async function docThan<T>(res: Response): Promise<T> {
  if (res.status === 204) return undefined as T;
  // `202 Accepted` của lệnh chạy nền (gửi email đặt lại mật khẩu) thường không có thân.
  if (res.status === 202 && !res.headers.get('Content-Type')?.includes('json')) return undefined as T;
  let than: string;
  try {
    than = await res.text();
  } catch {
    throw { code: 'NETWORK_ERROR', message: '' } satisfies ApiError;
  }
  // 200 thân rỗng là câu trả lời `null` của module NestJS: Nest gặp `null` thì gọi `response.send()` trơn, không
  // gửi chữ `null`. Ví dụ `GET /m/ekyc/ho-so/:id/giay-to` của phiên chưa bóc tách — coi nhầm là "chưa sẵn sàng"
  // thì màn báo dịch vụ hỏng trong khi nó vừa trả lời đúng.
  if (than.trim() === '') return null as T;
  try {
    return JSON.parse(than) as T;
  } catch {
    // 200 mà thân không phải JSON: đường dẫn `/api` chưa được định tuyến, máy
    // chủ trả về `index.html` của ứng dụng. Báo "chưa sẵn sàng" thay vì vỡ ở view.
    throw { code: API_CHUA_SAN_SANG, status: res.status, message: '' } satisfies ApiError;
  }
}

export async function goiApi<T>(duongDan: string, yeuCau: YeuCauApi = {}): Promise<T> {
  return docThan<T>(await gui(duongDan, yeuCau));
}

/** Mã lỗi khi người dùng tự huỷ lượt tải lên: phía gọi bỏ qua, không báo lỗi. */
export const DA_HUY_TAI_LEN = 'UPLOAD_CANCELLED';

/**
 * Gửi `multipart/form-data` kèm tiến trình, cho tệp lớn mà người dùng phải thấy đang đi tới đâu.
 *
 * `fetch` không báo tiến trình **gửi lên**, nên riêng đường này dùng `XMLHttpRequest`. Lỗi và thân phản hồi vẫn đi qua
 * `docLoi`, `docThan` như mọi lời gọi khác nên ra cùng một `ApiError`. Không tự thử lại khi `429`: gửi lại 500 MB
 * không phải việc nên làm ngầm.
 */
export async function goiApiBieuMauCoTienTrinh<T>(
  duongDan: string,
  bieuMau: FormData,
  tuyChon: { onTienTrinh?: (phanTram: number) => void; signal?: AbortSignal } = {},
): Promise<T> {
  let token: string | undefined;
  try {
    token = await getAccessToken();
  } catch {
    throw { code: 'UNAUTHENTICATED', status: 401, message: '' } satisfies ApiError;
  }
  const res = await new Promise<Response>((xong, hong) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', new URL(duongDan, window.location.origin));
    xhr.setRequestHeader('Accept', 'application/json, application/problem+json');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) tuyChon.onTienTrinh?.(Math.min(100, Math.round((e.loaded / e.total) * 100)));
    };
    xhr.onload = () => {
      const khongThan = xhr.status === 204 || xhr.status === 205;
      xong(new Response(khongThan ? null : xhr.responseText, {
        status: xhr.status,
        headers: { 'Content-Type': xhr.getResponseHeader('Content-Type') ?? '' },
      }));
    };
    xhr.onerror = () => hong({ code: 'NETWORK_ERROR', message: '' } satisfies ApiError);
    xhr.onabort = () => hong({ code: DA_HUY_TAI_LEN, message: '' } satisfies ApiError);
    if (tuyChon.signal?.aborted) {
      hong({ code: DA_HUY_TAI_LEN, message: '' } satisfies ApiError);
      return;
    }
    tuyChon.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(bieuMau);
  });
  if (!res.ok) throw await docLoi(res);
  return docThan<T>(res);
}

/**
 * Như `goiApi`, kèm header `ETag` của phản hồi — cho tài nguyên ghi có điều
 * kiện bằng `If-Match`. Thiếu `ETag` là backend sai hợp đồng: báo lỗi máy chủ
 * ngay, đừng để lần lưu sau gửi `If-Match` rỗng rồi nhận `428`.
 */
export async function goiApiKemEtag<T>(
  duongDan: string,
  yeuCau: YeuCauApi = {},
): Promise<{ du: T; etag: string }> {
  const res = await gui(duongDan, yeuCau);
  const etag = res.headers.get('ETag');
  if (!etag) throw { code: 'INTERNAL', status: res.status, message: '' } satisfies ApiError;
  return { du: await docThan<T>(res), etag };
}

/**
 * Tải một tệp nhị phân qua Gateway, kèm token như mọi lời gọi khác — cho đích
 * cần xác thực mà thẻ `<a href>` không gắn được `Authorization` (tệp license
 * theo vé, `GET /api/v1/license-downloads/{ticket}`).
 */
export async function goiApiTep(
  duongDan: string,
  accept = 'application/octet-stream, application/problem+json',
): Promise<Blob> {
  const res = await gui(duongDan, { accept });
  return res.blob();
}

/**
 * Tải tệp **kèm tên máy chủ đặt**: cho đích mà tên tệp do backend quyết (biên bản thẩm định, báo cáo xuất).
 *
 * Tên đọc từ `Content-Disposition`, ưu tiên `filename*=UTF-8''…` vì chỉ dạng đó mang được dấu tiếng Việt;
 * `filename=` thuần ASCII là bản dự phòng cho trình duyệt cũ. Không có header thì dùng `tenMacDinh` —
 * trình duyệt đặt tên theo URL sẽ cho ra một tệp tên là id của phiên.
 */
export async function goiApiTepCoTen(
  duongDan: string,
  tenMacDinh: string,
  accept = 'application/octet-stream, application/problem+json',
): Promise<{ blob: Blob; ten: string }> {
  const res = await gui(duongDan, { accept });
  return { blob: await res.blob(), ten: tenTuContentDisposition(res.headers.get('Content-Disposition')) || tenMacDinh };
}

/** Tên tệp trong `Content-Disposition`; chuỗi rỗng khi không đọc được. */
export function tenTuContentDisposition(header: string | null): string {
  if (!header) return '';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      // Phần trăm sai cú pháp: rơi xuống filename= thuần thay vì ném ra giữa lúc tải
    }
  }
  const ascii = /filename="([^"]*)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  return ascii?.[1]?.trim() ?? '';
}

/**
 * Lưu một `Blob` về máy người dùng với tên cho trước.
 *
 * Phải `revokeObjectURL` sau khi bấm: mỗi URL tạm giữ cả Blob trong bộ nhớ tới khi tab đóng, và một màn
 * hình tải mười biên bản là mười tệp nằm lại trong RAM.
 */
export function luuBlob(blob: Blob, ten: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = ten;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Tệp có thể tới theo hai dạng (ADR-0070 mục 1): kho `files/sign` trả `200` JSON
 * `{url, expires_at, file_name, mime_type}` để trình duyệt tải thẳng từ object
 * storage; kho tạm của module (bản dev) trả thẳng nội dung tệp. Hàm này đọc
 * `Content-Type` để biết là dạng nào, không đoán theo môi trường.
 */
export async function goiApiTepHoacLienKet(
  duongDan: string,
): Promise<
  | { kieu: 'lien-ket'; url: string; file_name: string; mime_type: string }
  | { kieu: 'noi-dung'; blob: Blob; mime_type: string }
> {
  const res = await gui(duongDan, { accept: '*/*' });
  const loai = res.headers.get('Content-Type') ?? '';
  if (loai.includes('application/json')) {
    const du = (await res.json()) as { url?: string; file_name?: string; mime_type?: string };
    if (!du.url) throw { code: 'INTERNAL', status: res.status, message: '' } satisfies ApiError;
    return { kieu: 'lien-ket', url: du.url, file_name: du.file_name ?? '', mime_type: du.mime_type ?? '' };
  }
  const blob = await res.blob();
  return { kieu: 'noi-dung', blob, mime_type: loai.split(';')[0] ?? '' };
}
